import './bottom-tab-bar.scss';

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';

import { Car, Grid, Home, Key, User } from '@/media/icons';
import useAppNavigate from '@/shared/hooks/useAppNavigation';
import { ROUTES, WEBVIEW_ROUTES } from '@/shared/routes';
import { pressServiceTab } from '@/shared/widgets/iframe-bridge';
import { useAppSelector } from '@/store';

type TIconComponent = React.FC<React.SVGProps<SVGSVGElement>>;

interface IShellTab {
  id: string;
  route: string;
  Icon: TIconComponent;
  labelKey: string;
  shouldShow: boolean;
}

/** Иконки для service-табов по имени (как нативный iconByName). */
const ICON_BY_NAME: Record<string, TIconComponent> = {
  home: Home,
  grid: Grid,
  user: User,
  car: Car,
  key: Key
};

const SERVICE_ROUTE_SET = new Set<string>(Object.values(WEBVIEW_ROUTES));

/**
 * Нижняя навигация. Читает табы из глобального состояния (`ui.tabBar`): если сервис задал
 * свои табы через мост — рисует их (клик возвращается сервису), иначе — дефолтные табы
 * оболочки. Внутри сервиса без своих табов скрывается (паритет с нативным `tabBar:'hidden'`).
 */
const BottomTabBar: React.FC = () => {
  const { t } = useTranslation();
  const { navigate } = useAppNavigate();
  const { pathname } = useLocation();
  const { config, visible } = useAppSelector((state) => state.ui.tabBar);
  const { isAuthorized } = useAppSelector((state) => state.auth);

  // /{lang}/{segment}/... → segment
  const segment = pathname.split('/')[2] ?? '';
  const isServiceRoute = SERVICE_ROUTE_SET.has(segment);

  /** Дефолтные табы оболочки. */
  const SHELL_TABS: IShellTab[] = useMemo(
    () => [
      {
        id: 'home',
        route: ROUTES.home,
        Icon: Home,
        labelKey: 'tabs.home',
        shouldShow: true
      },
      {
        id: 'vehicle-management',
        route: WEBVIEW_ROUTES.vehicleManagement,
        Icon: Car,
        labelKey: 'tabs.vehicle-management',
        shouldShow: isAuthorized
      },
      {
        id: 'services',
        route: ROUTES.services,
        Icon: Grid,
        labelKey: 'tabs.services',
        shouldShow: true
      },
      {
        id: 'profile',
        route: ROUTES.profile,
        Icon: User,
        labelKey: 'tabs.profile',
        shouldShow: true
      }
    ],
    [isAuthorized]
  );

  // Сервис задал свои табы — рисуем их, клик возвращаем сервису событием tabBar.pressed.
  if (config) {
    if (!visible || !config.items?.length) return null;
    return (
      <nav className="bottom-tab-bar">
        {config.items.map((item) => {
          const Icon = item.icon ? ICON_BY_NAME[item.icon] : undefined;
          const active = item.id === config.activeId;
          return (
            <button
              key={item.id}
              type="button"
              className={`bottom-tab-bar__tab${active ? ' bottom-tab-bar__tab--active' : ''}`}
              onClick={() => pressServiceTab(item.id)}
            >
              {Icon && <Icon className="bottom-tab-bar__icon" />}
              <span className="bottom-tab-bar__label">{item.label}</span>
              {item.badge != null && (
                <span className="bottom-tab-bar__badge">{item.badge}</span>
              )}
            </button>
          );
        })}
      </nav>
    );
  }

  // В сервисе без своих табов таб-бар скрыт.
  if (isServiceRoute) return null;

  // Экраны оболочки — дефолтные табы.
  return (
    <nav className="bottom-tab-bar">
      <div className="bottom-tab-bar__tabs">
        {SHELL_TABS.map(({ id, route, Icon, labelKey, shouldShow }) => {
          if (!shouldShow) return null;
          const active = segment === route;
          return (
            <button
              key={id}
              type="button"
              className={`bottom-tab-bar__tab${active ? ' bottom-tab-bar__tab--active' : ''}`}
              onClick={() => navigate(`/${route}`)}
            >
              <Icon className="bottom-tab-bar__icon" />
              <span className="bottom-tab-bar__label">{t(labelKey)}</span>
            </button>
          );
        })}
      </div>
    </nav>
  );
};

export default BottomTabBar;
